import { useCallback, useState } from "react";

import {
    LAYOUT_STORAGE_KEY,
    type MeetingLayoutId,
    defaultMeetingLayout,
    focusedLayout,
    isMeetingLayoutId,
} from "../domain/meetingLayout";
import { useRememberedChoice } from "./rememberedChoice";

export type MeetingLayoutChannel = {
    /** What this browser remembers, and what the call returns to. */
    layout: MeetingLayoutId;
    /** What is on screen right now — the choice, or the share's focus. */
    presented: MeetingLayoutId;
    setLayout: (layout: MeetingLayoutId) => void;
    /** Whether a shared screen is the one deciding the arrangement. */
    autoFocused: boolean;
};

/**
 * Which arrangement of the picture this person is looking at.
 *
 * Entirely local. Two people in the same call can be in different layouts,
 * which is the point: what you need to see is not what they need to see.
 *
 * A shared screen is the one thing that arranges the picture on its own, and
 * only for as long as it is up. The choice underneath is never overwritten by
 * it, so the call returns to it the moment the share stops.
 *
 * Pressing a layout while a share is running does two things: it wins for the
 * rest of that share, and it is remembered like any other press. Not two
 * kinds of memory behind one control — a radio in a menu is a statement about
 * how this person wants to watch calls, and it would be impossible to explain
 * a choice that quietly expires. What does expire is the pin: the next share
 * focuses again, which is what "the shared screen comes up by itself" has to
 * mean to be worth anything.
 *
 * `compact` moves only the starting point — a phone opens on the other
 * person rather than on a grid of stamps. It is read once, when the room
 * opens: turning a handset sideways mid-session is not a request to
 * rearrange the call, and a remembered choice outranks it either way.
 */
export function useMeetingLayout(
    sharing = false,
    compact = false
): MeetingLayoutChannel {
    const [layout, choose] = useRememberedChoice<MeetingLayoutId>(
        LAYOUT_STORAGE_KEY,
        isMeetingLayoutId,
        defaultMeetingLayout(compact)
    );

    /** Whether this person has overridden the focus for the share on now. */
    const [pinned, setPinned] = useState(false);
    // A share starting and a share ending both clear the override, adjusted
    // during the render that notices rather than in an effect, so the picture
    // never flashes the layout that is on its way out.
    const [wasSharing, setWasSharing] = useState(sharing);
    if (wasSharing !== sharing) {
        setWasSharing(sharing);
        if (pinned) setPinned(false);
    }

    const setLayout = useCallback(
        (next: MeetingLayoutId) => {
            choose(next);
            if (sharing) setPinned(true);
        },
        [choose, sharing]
    );

    return {
        layout,
        presented: focusedLayout(layout, { sharing, pinned }),
        setLayout,
        autoFocused: sharing && !pinned,
    };
}
